import { Icon } from './icons';

/**
 * The closed door.
 *
 * Valuations has no login of its own by design: identity, entitlement and
 * T&C acceptance are all decided by the central licensing gate on
 * acumon-intelligence.com, and this app only ever verifies the token that
 * gate mints. So the honest thing to show someone without a session is not
 * a form, but the way back to the front door.
 */
export function Gate({
  launchUrl,
  message,
  devLaunchAvailable,
}: {
  launchUrl: string;
  message?: string;
  devLaunchAvailable: boolean;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
      <div className="w-full max-w-lg">
        <div className="card overflow-hidden">
          <div className="h-1 w-full bg-gradient-to-r from-teal-500 to-teal-600" />
          <div className="px-8 py-10">
            <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-xl bg-teal-50">
              <Icon name="lock" className="h-7 w-7 text-teal-600" />
            </div>

            <h1 className="text-xl font-semibold text-slate-900">Valuations is gated</h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">
              {message ??
                'This tool is opened from acumon-intelligence.com. The launch gate checks your sign-in, your firm’s entitlement and the current terms before it lets you through.'}
            </p>

            <ol className="mt-6 space-y-3">
              {[
                'Sign in on acumon-intelligence.com',
                'Entitlement checked against your firm’s licence',
                'Current product terms accepted',
                'Session issued and you land here',
              ].map((step, i) => (
                <li key={step} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[11px] font-semibold text-slate-500">
                    {i + 1}
                  </span>
                  <span className="text-sm text-slate-600">{step}</span>
                </li>
              ))}
            </ol>

            <a href={launchUrl} className="btn-primary mt-8 w-full">
              Open from Acumon Intelligence
              <Icon name="external" className="h-4 w-4" />
            </a>

            {devLaunchAvailable ? (
              <div className="mt-6 rounded-xl border border-dashed border-slate-200 bg-slate-50/70 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                  Local development
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  The dev launch simulator mints the same token shape the licensing gate mints, so
                  the real verification path is exercised locally.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a
                    className="btn-secondary text-xs"
                    href="/api/dev/launch?email=valuer@acumon.com&name=Rachel%20Okonjo"
                  >
                    Launch as preparer
                  </a>
                  <a
                    className="btn-secondary text-xs"
                    href="/api/dev/launch?email=reviewer@acumon.com&name=David%20Mensah"
                  >
                    Launch as Acumon reviewer
                  </a>
                  <a
                    className="btn-secondary text-xs"
                    href="/api/dev/launch?email=viewer@brightwell.co.uk&name=Sam%20Ellis&readonly=1"
                  >
                    Launch read-only
                  </a>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">
          Acumon Valuation Engine · valuations.acumon-intelligence.com
        </p>
      </div>
    </div>
  );
}
